'use client';

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, FieldError, SingleSelect } from '@galapagos/design-system/web';
import { ONBOARDING_INTAKE_FLOW_ID } from '@galapagos/shared';
import { FormActions } from '../components/FormActions';
import { PageTitle } from '../components/PageTitle';
import { SignupShell } from '../components/SignupShell';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { useSignupApi } from '../hooks/useSignupApi';
import { useStepHeading } from '../hooks/useStepHeading';
import { quizAnswer, type WordedQuizQuestion } from '../services/quiz-content';

export interface QuizQuestionScreenProps {
  question: WordedQuizQuestion;
  /** Called once the answer is saved; the caller moves the flow on. */
  onAnswered: () => void;
  /** A short line above the options saying why the question is asked. */
  context?: string;
  /** The browser tab's name when the question title must not reach it. */
  documentTitle?: string;
}

type StatusMessage = 'none' | 'required' | 'saveError';

/**
 * One single-choice quiz question, drawn entirely from its content: title,
 * description, an option card per option, and Continue. The answer is saved
 * to the member's intake onboarding session before the flow moves on;
 * nothing about a particular question lives here.
 */
export function QuizQuestionScreen({
  question,
  onAnswered,
  context,
  documentTitle,
}: QuizQuestionScreenProps) {
  const { t } = useTranslation('signup');
  const api = useSignupApi();
  const headingRef = useStepHeading(question.title, documentTitle);
  const [value, setValue] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<StatusMessage>('none');
  // A ref, not state, so a second press in the same tick still sees the save.
  const savingRef = useRef(false);
  ensureSignupStepStyle();

  const handleSelect = (next: string) => {
    // A save is on its way with the earlier choice; a change now would show
    // one answer and save another, so the choice holds until it settles.
    if (savingRef.current) return;
    setValue(next);
    setStatus('none');
  };

  const handleContinue = async () => {
    if (savingRef.current) return;
    if (value === null) {
      setStatus('required');
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setStatus('none');
    try {
      await api.answerOnboarding(ONBOARDING_INTAKE_FLOW_ID, {
        answers: quizAnswer(question, value),
      });
    } catch {
      savingRef.current = false;
      setSaving(false);
      setStatus('saveError');
      return;
    }
    onAnswered();
  };

  const statusText =
    status === 'required' ? t('quiz.required') : status === 'saveError' ? t('quiz.saveError') : '';

  return (
    <SignupShell>
      <div className="signup-step">
        <PageTitle
          title={question.title}
          description={question.description}
          headingRef={headingRef}
        />
        {/* The answers and the context line are health information:
            ph-no-capture keeps PostHog's autocapture away from them, as the
            substance step does. */}
        <div className="signup-step__group ph-no-capture" data-testid="signup-quiz-capture-guard">
          {context ? (
            <div data-testid="signup-quiz-context">
              {/* analytics-exempt: non-user-control — a static label chip with no press handler. */}
              <Chip label={context} variant="Subtle" size="S" iconName="info" />
            </div>
          ) : null}
          <SingleSelect
            actionId="signup.quiz-option.change"
            label={question.title}
            hideLabel
            value={value}
            options={question.options.map((option) => ({
              value: option.value,
              label: option.label,
              ...(option.description ? { description: option.description } : {}),
            }))}
            invalid={status === 'required'}
            onSelect={handleSelect}
            data-testid="signup-quiz-options"
          />
          {/* Always mounted, so a message that appears inside it is announced. */}
          <div aria-live="polite" data-testid="signup-quiz-status">
            <FieldError message={statusText} live={false} data-testid="signup-quiz-error" />
          </div>
        </div>
        <FormActions>
          <Button
            size="L"
            type="Primary"
            state={saving ? 'Loading' : 'Default'}
            label={t('actions.continue')}
            onPress={() => void handleContinue()}
            actionId="signup.quiz-continue.activate"
            data-testid="signup-quiz-continue"
          />
        </FormActions>
      </div>
    </SignupShell>
  );
}
